import type { ReactNode } from 'react';
import { Avatar, Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { PageHeader } from '@/shared/ui/components/page-header';
import { FormActionBar } from '@/shared/ui/components/form-action-bar';
import { HeaderTabs } from '@/shared/ui/components/header-tabs';

/**
 * Visual chrome for the practitioner edit page (PROD-761/762/763/764).
 * Mirrors the StaffEditShell / PracticeEditShell pattern — breadcrumb +
 * back-arrow + image-or-initials avatar + name + per-tab navigation +
 * Save button slot.
 *
 * Pure presentation. The owning `PractitionerEditPage` passes everything in.
 *
 * Tab visibility (Flutter `doctor_base_widget.dart:129-253`):
 *   isNotBoarded:       Personal Details + Account Details ONLY (2 tabs)
 *   isBoarded (default): all 6 base tabs (Personal / Account / Professional /
 *                       Educational / Schedule / Practices) + 2 conditional
 *   Conditional:
 *     Billing — only if user has `Billing_Settings` view permission
 *     Macro   — only when current user.role === OrgAdmin (PM context)
 *     Order sets — straight after Macro; physician on own settings (PROD-2710,
 *                  `canSeeOrderSetsTab` in settings-tabs.ts)
 */
export type PractitionerTabId =
  | 'personal'
  | 'account'
  | 'professional'
  | 'educational'
  | 'schedule'
  | 'practices'
  | 'billing'
  | 'macro'
  | 'orderSets';

interface PractitionerTabSpec {
  id: PractitionerTabId;
  label: string;
}

const ALL_TABS: readonly PractitionerTabSpec[] = [
  { id: 'personal',     label: 'Personal details' },
  { id: 'account',      label: 'Account details' },
  { id: 'professional', label: 'Professional details' },
  { id: 'educational',  label: 'Educational details' },
  { id: 'schedule',     label: 'Schedule' },
  { id: 'practices',    label: 'Practices' },
  { id: 'billing',      label: 'Billing' },
  { id: 'macro',        label: 'Macro' },
  { id: 'orderSets',    label: 'Order sets' },
];

export interface PractitionerEditShellProps {
  mode: 'create' | 'edit';
  activeTab: PractitionerTabId;
  onTabChange: (next: PractitionerTabId) => void;
  isNotBoarded: boolean;
  canSeeBilling: boolean;
  canSeeMacro: boolean;
  /** PROD-2710 — `canSeeOrderSetsTab` (settings-tabs.ts). Absent → hidden. */
  canSeeOrderSets?: boolean;
  practitionerName: string;
  photoUrl: string | null;
  onBack: () => void;
  saveLabel: string;
  onSave: () => void;
  saveDisabled: boolean;
  isLoading: boolean;
  /**
   * Account Settings mode (Flutter `isFromAccountSettings`). When true the
   * page is the logged-in practitioner editing their OWN record: hide the
   * back arrow + "PRACTITIONER" list link (`showBack: !isFromAccountSettings`,
   * doctor_base_widget.dart:1248) and label the breadcrumb ACCOUNT SETTINGS.
   */
  accountMode?: boolean;
  children: ReactNode;
}

export function PractitionerEditShell({
  mode,
  activeTab,
  onTabChange,
  isNotBoarded,
  canSeeBilling,
  canSeeMacro,
  canSeeOrderSets = false,
  practitionerName,
  photoUrl,
  onBack,
  saveLabel,
  onSave,
  saveDisabled,
  isLoading,
  accountMode = false,
  children,
}: PractitionerEditShellProps) {
  const tabs = ALL_TABS.filter((t) => {
    if (isNotBoarded) return t.id === 'personal' || t.id === 'account';
    if (t.id === 'billing') return canSeeBilling;
    if (t.id === 'macro') return canSeeMacro;
    if (t.id === 'orderSets') return canSeeOrderSets;
    return true;
  });
  const active = tabs.find((t) => t.id === activeTab) ?? tabs[0];

  return (
    <div data-page="settings-page" className="flex flex-col h-full overflow-hidden">
      {/* The one page header (like every page): the photo, the name over no subline, the tabs on the right
          (what does not fit goes into More). Back and Save sit below the content in the form action bar (Antonin, 15 Oct). The breadcrumb lives in the top bar only. */}
      <PageHeader
        leading={
          <Avatar
            scale="compact"
            tone="provider"
            name={mode === 'create' ? undefined : practitionerName}
            alt={practitionerName}
            src={photoUrl ?? undefined}
            size={48}
          />
        }
        title={mode === 'create' ? 'New practitioner' : practitionerName}
        tabs={
          <HeaderTabs<PractitionerTabId>
            tabs={tabs.map((t) => ({ id: t.id, label: t.label }))}
            value={active.id}
            onChange={onTabChange}
            aria-label="Practitioner edit tabs"
          />
        }
      />

      <div data-slot="settings-body" className="flex-1 overflow-auto scrollbar-hide">
        {isLoading ? (
          <BrandLoading size={64} label="Loading practitioner…" />
        ) : (
          <div className="max-w-160">{children}</div>
        )}
        <FormActionBar>
          {!accountMode && (
            <Button type="button" variant="tertiary" size="sm" onClick={onBack} aria-label="Back to practitioners list">
              Back
            </Button>
          )}
          <Button type="button" size="sm" onClick={onSave} disabled={saveDisabled}>
            {saveLabel}
          </Button>
        </FormActionBar>
      </div>
    </div>
  );
}
